import { useTranslation } from "react-i18next";
import {
    Button,
    Input,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tabs,
    TabsList,
    TabsTrigger,
} from "frontend-components";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
} from "@tanstack/react-table";
import { faArrowDownWideShort, faArrowUpWideShort } from "@fortawesome/pro-regular-svg-icons";
import { faMagnifyingGlass, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { getListTaskCounts, type ListRow } from "./utils";
import { CompletionIndicator, ProgressFill } from "./ListProgress";
import { NewListButton } from "./NewListButton";
import ListCard from "./ListCard";
import TemplateCard from "./TemplateCard";
import Pagination from "../AnnualCycle/Pagination";
import { useDeleteList } from "@dashboard/hooks/useListQuery";
import { useListsWithTasks } from "@dashboard/hooks/useListWithTasks";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { Can, TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { useAbility } from "@casl/react";
import { subject } from "@casl/ability";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import { formatDateShort } from "@dashboard/utils/formatDate";

type StatusFilter = "all" | "active" | "completed";
type ListsView = "checklists" | "templates";
type SearchableListRow = ListRow & { responsibleName: string; createdAtLabel: string };

const SKELETON_CARD_COUNT = 5;

export default function ListsTable() {
    const { t } = useTranslation();
    const navigate = useNavigate({ from: "/$customerId/dashboard/$profileId/lists" });
    const { view = "checklists" } = useSearch({
        from: "/_hub/$customerId/dashboard/$profileId/lists",
    });

    return (
        <div className="flex flex-col gap-6">
            <Tabs
                value={view}
                onValueChange={(next: ListsView) =>
                    void navigate({
                        to: ".",
                        search: (prev) => ({
                            ...prev,
                            view: next === "templates" ? next : undefined,
                            status: undefined,
                            search: undefined,
                        }),
                    })
                }
            >
                <TabsList
                    variant="line"
                    className="w-full justify-start gap-6 border-b border-compendia-border"
                >
                    <TabsTrigger value="checklists" className="flex-none px-0">
                        {t("common.checklists")}
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="flex-none px-0">
                        {t("list.templates")}
                    </TabsTrigger>
                </TabsList>
            </Tabs>
            <ListsTableView key={view} view={view} />
        </div>
    );
}

function ListsTableView({ view }: { view: ListsView }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate({ from: "/$customerId/dashboard/$profileId/lists" });
    const { status, search } = useSearch({
        from: "/_hub/$customerId/dashboard/$profileId/lists",
    });
    const isTemplates = view === "templates";
    const statusFilter: StatusFilter = status ?? "all";
    const setStatusFilter = useCallback(
        (next: StatusFilter) =>
            void navigate({
                to: ".",
                search: (prev) => ({ ...prev, status: next === "all" ? undefined : next }),
            }),
        [navigate],
    );
    const searchValue = search ?? "";
    const setSearchValue = useCallback(
        (next: string) =>
            void navigate({
                to: ".",
                search: (prev) => ({ ...prev, search: next || undefined }),
            }),
        [navigate],
    );
    const [listToDelete, setListToDelete] = useState<number | null>(null);
    const deleteMutation = useDeleteList();
    const { instanceId } = useHseInstance();
    const ability = useAbility(TaskAbilityContext);
    const { data: listTableData, isLoading, isError } = useListsWithTasks({ instanceId });

    const tableData: SearchableListRow[] = useMemo(() => {
        if (isLoading) {
            return Array(8).fill({} as SearchableListRow);
        }
        const rows = (listTableData ?? []).map((list) => {
            const { completedTasks, totalTasks } = getListTaskCounts(list);
            const isCompleted = totalTasks > 0 && completedTasks === totalTasks;
            const progressPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
            return {
                ...list,
                completedTasks,
                totalTasks,
                isCompleted,
                progressPercent,
                responsibleName: list.responsibleUser
                    ? `${list.responsibleUser.firstName} ${list.responsibleUser.lastName}`
                    : "",
                createdAtLabel: list.createdAt
                    ? formatDateShort(new Date(list.createdAt), i18n.language)
                    : "",
            };
        });

        const shown = rows.filter((r) => r.template === isTemplates);
        if (isTemplates || statusFilter === "all") return shown;
        if (statusFilter === "completed") return shown.filter((r) => r.isCompleted);
        return shown.filter((r) => !r.isCompleted);
    }, [isLoading, listTableData, statusFilter, isTemplates, i18n.language]);

    const openList = useCallback(
        (id: number) =>
            void navigate({
                to: ".",
                search: (prev) => ({ ...prev, list: id, editList: undefined }),
            }),
        [navigate],
    );

    const applyTemplate = useCallback(
        (id: number) =>
            void navigate({
                to: ".",
                search: (prev) => ({ ...prev, list: "new", listFrom: id, editList: undefined }),
            }),
        [navigate],
    );

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<SearchableListRow>();
        const title = columnHelper.accessor("title", {
            header: () => t("common.title"),
            cell: (info) => (
                <button
                    type="button"
                    className="block w-full cursor-pointer truncate text-left text-sm font-semibold text-compendia-foreground hover:underline"
                    onClick={() => openList(info.row.original.id)}
                >
                    {info.getValue()}
                </button>
            ),
            meta: { className: "max-w-0 min-w-[200px]" },
        });

        if (isTemplates) {
            return [
                title,
                columnHelper.display({
                    id: "tasks",
                    header: () => t("common.tasks"),
                    cell: (info) => (
                        <span className="text-sm text-compendia-muted">
                            {info.row.original.totalTasks}
                        </span>
                    ),
                    meta: { className: "w-24" },
                }),
                columnHelper.accessor("updatedAt", {
                    id: "updatedAt",
                    header: () => t("list.lastModified"),
                    cell: (info) => (
                        <span className="text-sm text-compendia-muted">
                            {formatDateShort(new Date(info.getValue()), i18n.language)}
                        </span>
                    ),
                    meta: { className: "w-40" },
                }),
                columnHelper.display({
                    id: "useTemplate",
                    header: () => null,
                    cell: (info) => (
                        <Can I="create" this={subject("TaskList", { instanceId })}>
                            <Button
                                type="button"
                                className="px-4 py-1.5 text-sm"
                                onClick={() => applyTemplate(info.row.original.id)}
                            >
                                {t("list.useTemplate")}
                            </Button>
                        </Can>
                    ),
                    meta: { className: "w-40 text-right" },
                }),
            ] as ColumnDef<SearchableListRow>[];
        }

        return [
            title,
            columnHelper.display({
                id: "progress",
                header: () => t("common.progress"),
                cell: (info) => <ProgressFill row={info.row.original} />,
                meta: { className: "w-56" },
            }),
            columnHelper.display({
                id: "tasks",
                header: () => t("common.tasks"),
                cell: (info) => (
                    <span className="text-sm text-compendia-muted">
                        {info.row.original.completedTasks}/{info.row.original.totalTasks}
                    </span>
                ),
                meta: { className: "w-24" },
            }),
            columnHelper.accessor("createdAt", {
                id: "createdAt",
                header: () => t("common.date"),
                cell: (info) => (
                    <span className="text-sm text-compendia-muted">
                        {formatDateShort(new Date(info.getValue()), i18n.language)}
                    </span>
                ),
                meta: { className: "w-32" },
            }),
            columnHelper.accessor("responsibleName", {
                id: "createdBy",
                header: () => t("common.responsible"),
                cell: (info) =>
                    info.getValue() ? (
                        <span className="text-sm text-compendia-foreground">{info.getValue()}</span>
                    ) : (
                        <span className="text-sm text-compendia-muted">-</span>
                    ),
                meta: { className: "w-44" },
            }),
            columnHelper.display({
                id: "completed",
                header: () => (
                    <div className="w-full flex justify-center">{t("common.completed")}</div>
                ),
                cell: (info) => (
                    <div className="w-full flex justify-center">
                        <CompletionIndicator isCompleted={info.row.original.isCompleted} />
                    </div>
                ),
                meta: { className: "w-24" },
            }),
            columnHelper.display({
                id: "delete",
                header: () => null,
                cell: (info) => (
                    <Can
                        I="delete"
                        this={subject("TaskList", {
                            instanceId: info.row.original.instanceId,
                            createdById: info.row.original.createdById,
                        })}
                    >
                        <button
                            type="button"
                            aria-label={t("common.deleteList")}
                            className="cursor-pointer w-full text-compendia-muted hover:text-chameleon-pink"
                            onClick={(e) => {
                                e.stopPropagation();
                                setListToDelete(info.row.original.id);
                            }}
                        >
                            <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                    </Can>
                ),
                size: 40,
                meta: { className: "w-12" },
            }),
        ] as ColumnDef<SearchableListRow>[];
    }, [t, i18n.language, openList, applyTemplate, isTemplates, instanceId]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map((column) => ({
                      ...column,
                      cell: () => (
                          <div className="w-full bg-compendia-foreground/20 h-5 rounded-full animate-pulse" />
                      ),
                  }))
                : columns,
        [columns, isLoading],
    );

    const sortColumnId = isTemplates ? "updatedAt" : "createdAt";
    const table = useReactTable<SearchableListRow>({
        data: tableData,
        columns: tableColumns,
        state: {
            globalFilter: searchValue,
            columnVisibility: { delete: ability.can("delete", "TaskList") },
        },
        globalFilterFn: (row, _columnId, filterValue) => {
            const searchTerm = String(filterValue).toLowerCase();
            if (!searchTerm || isLoading) return true;

            const list = row.original;
            const searchableFields = [
                (list.title ?? "").toLowerCase(),
                list.createdAtLabel,
                list.responsibleName.toLowerCase(),
            ];
            return searchableFields.some((field) => field.includes(searchTerm));
        },
        initialState: {
            pagination: { pageSize: 15, pageIndex: 0 },
            sorting: [{ id: sortColumnId, desc: true }],
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const rows = table.getRowModel().rows;
    const sortColumn = table.getColumn(sortColumnId);
    const sortDirection = sortColumn?.getIsSorted();

    if (isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <p className="text-chameleon-pink text-base">{t("common.errorLoadingLists")}</p>
            </div>
        );
    }

    const filterChipBase =
        "inline-flex items-center px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer";
    const activeChip = "bg-compendia-foreground text-white";
    const inactiveChip =
        "bg-compendia-greige-20 text-compendia-foreground hover:bg-compendia-greige-40";

    const filterButton = (key: StatusFilter, label: string) => (
        <button
            type="button"
            onClick={() => setStatusFilter(key)}
            className={`${filterChipBase} ${statusFilter === key ? activeChip : inactiveChip}`}
            aria-pressed={statusFilter === key}
        >
            {label}
        </button>
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center xl:justify-between">
                <div className="flex flex-1 items-center gap-3">
                    <div className="relative flex-1 xl:min-w-[280px]">
                        <FontAwesomeIcon
                            icon={faMagnifyingGlass}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-chameleon-green pointer-events-none"
                        />
                        <Input
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={t(
                                isTemplates ? "common.searchTemplates" : "common.searchChecklists",
                            )}
                            className="pl-9 w-full"
                        />
                    </div>
                    <NewListButton iconOnly template={isTemplates} className="xl:hidden" />
                </div>
                {!isTemplates && (
                    <div className="flex flex-wrap items-center gap-2">
                        {filterButton("all", t("common.all"))}
                        {filterButton("active", t("common.active"))}
                        {filterButton("completed", t("common.completed"))}
                    </div>
                )}
                <NewListButton template={isTemplates} className="hidden xl:inline-flex" />
            </div>
            <div className="flex flex-col gap-3 xl:hidden">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-category font-medium uppercase tracking-category text-compendia-muted">
                        {isTemplates ? t("list.templates") : t("page.checklists")}
                    </span>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => sortColumn?.toggleSorting(sortDirection === "asc")}
                            className="flex cursor-pointer items-center gap-1.5 text-label text-compendia-muted transition-colors hover:text-chameleon-green"
                        >
                            {isTemplates ? t("list.lastModified") : t("common.date")}
                            <FontAwesomeIcon
                                icon={
                                    sortDirection === "asc"
                                        ? faArrowUpWideShort
                                        : faArrowDownWideShort
                                }
                            />
                        </button>
                        <span className="text-label text-compendia-muted">
                            {t("common.total")}:{" "}
                            <span className="font-semibold text-compendia-foreground">
                                {table.getFilteredRowModel().rows.length}
                            </span>
                        </span>
                    </div>
                </div>

                {isLoading ? (
                    Array.from({ length: SKELETON_CARD_COUNT }, (_, idx) => (
                        <Skeleton key={idx} className="h-[104px] rounded-[3px]" />
                    ))
                ) : rows.length ? (
                    <ul className="flex flex-col gap-3">
                        {rows.map((row) =>
                            isTemplates ? (
                                <TemplateCard
                                    key={row.id}
                                    list={row.original}
                                    onOpen={openList}
                                    onUse={applyTemplate}
                                />
                            ) : (
                                <ListCard key={row.id} list={row.original} onOpen={openList} />
                            ),
                        )}
                    </ul>
                ) : (
                    <div className="rounded-[3px] border border-compendia-border bg-compendia-card py-8 text-center text-compendia-muted">
                        {t("common.no_results")}
                    </div>
                )}
            </div>

            <div className="hidden rounded-[3px] border border-compendia-border overflow-hidden bg-compendia-card xl:block">
                <Table className="w-full border-collapse">
                    <TableHeader className="bg-compendia-greige-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow
                                key={headerGroup.id}
                                className="border-b border-compendia-border hover:bg-transparent"
                            >
                                {headerGroup.headers.map((header) => {
                                    const headerMeta = header.column.columnDef.meta as
                                        | { className?: string }
                                        | undefined;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={`text-compendia-foreground text-xs font-semibold uppercase tracking-wider ${
                                                headerMeta?.className ?? ""
                                            }`}
                                        >
                                            {header.column.getCanSort() ? (
                                                <button
                                                    type="button"
                                                    className="w-full flex items-center hover:text-chameleon-green transition-colors"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                    {header.column.getIsSorted() === "desc" && (
                                                        <FontAwesomeIcon
                                                            className="pl-1"
                                                            icon={faArrowDownWideShort}
                                                        />
                                                    )}
                                                    {header.column.getIsSorted() === "asc" && (
                                                        <FontAwesomeIcon
                                                            className="pl-1"
                                                            icon={faArrowUpWideShort}
                                                        />
                                                    )}
                                                </button>
                                            ) : header.isPlaceholder ? null : (
                                                flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className="border-b border-compendia-border h-14 hover:bg-compendia-greige-10 transition-colors last:border-b-0"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const cellMeta = cell.column.columnDef.meta as
                                            | { className?: string }
                                            | undefined;
                                        return (
                                            <TableCell
                                                key={cell.id}
                                                className={cellMeta?.className ?? ""}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={table.getAllColumns().length}
                                    className="text-center text-compendia-muted py-8"
                                >
                                    {t("common.no_results")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <Pagination table={table} />

            <ConfirmDeleteDialog
                open={listToDelete !== null}
                onOpenChange={(open) => {
                    if (!open) setListToDelete(null);
                }}
                onConfirm={async () => {
                    if (listToDelete !== null) {
                        await deleteMutation.mutateAsync(listToDelete);
                        setListToDelete(null);
                    }
                }}
                title={t("common.deleteList")}
                description={t("common.confirmDeleteList")}
            />
        </div>
    );
}
